import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldAlert, CheckCircle2, RotateCcw, Flag } from "lucide-react";

function fmt(iso) {
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function AdminDenuncias() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/"); return; }
  }, [user, navigate]);

  const load = () => {
    setLoading(true);
    api.get("/admin/reports").then(({ data }) => setReports(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user]);

  const toggle = async (id) => {
    try {
      const { data } = await api.patch(`/admin/reports/${id}`);
      setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status: data.status } : r)));
      toast.success(data.status === "resolved" ? "Denúncia resolvida." : "Denúncia reaberta.");
    } catch { toast.error("Não foi possível atualizar."); }
  };

  const shown = reports.filter((r) => (filter === "all" ? true : r.status === filter));
  const openCount = reports.filter((r) => r.status === "open").length;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8" data-testid="admin-denuncias-page">
      <p className="overline text-blaze mb-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Painel admin</p>
      <h1 className="font-heading font-800 text-3xl md:text-4xl mb-1">Denúncias</h1>
      <p className="text-sm text-muted-foreground mb-6">{openCount} em aberto · {reports.length} no total</p>

      <div className="flex gap-2 mb-5">
        {[["open", "Em aberto"], ["resolved", "Resolvidas"], ["all", "Todas"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`px-4 py-2 text-sm font-600 rounded-sm border transition-colors ${filter === k ? "bg-obsidian text-white border-obsidian" : "border-border hover:bg-secondary"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blaze" /></div>
      ) : shown.length === 0 ? (
        <div className="text-center py-20 text-sm text-muted-foreground border border-dashed border-border rounded-sm" data-testid="admin-empty">
          <Flag className="w-8 h-8 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
          Nenhuma denúncia {filter === "open" ? "em aberto" : filter === "resolved" ? "resolvida" : ""}.
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => (
            <div key={r.id} className="bg-white border border-border rounded-sm p-5" data-testid={`report-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block px-2 py-0.5 rounded-sm bg-blaze/10 text-blaze text-xs font-700" data-testid={`report-reason-${r.id}`}>{r.reason}</span>
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-700 ${r.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.status === "resolved" ? "Resolvida" : "Em aberto"}
                    </span>
                  </div>
                  <p className="text-sm font-600">
                    <span className="text-muted-foreground">Denunciado:</span> {r.reported_name || r.reported_id}
                    <span className="text-muted-foreground"> · por </span>{r.reporter_name || r.reporter_id}
                  </p>
                  <button className="text-xs text-blaze hover:underline mt-0.5" onClick={() => navigate(`/anuncio/${r.listing_id}`)} data-testid={`report-listing-${r.id}`}>
                    {r.listing_title}
                  </button>
                  {r.note && <p className="text-sm text-foreground mt-2 bg-secondary/50 rounded-sm px-3 py-2 whitespace-pre-wrap break-words">{r.note}</p>}
                  <p className="text-[11px] text-muted-foreground mt-2">{fmt(r.created_at)}</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-sm shrink-0" onClick={() => toggle(r.id)} data-testid={`report-toggle-${r.id}`}>
                  {r.status === "resolved" ? <><RotateCcw className="w-4 h-4 mr-1" /> Reabrir</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Marcar resolvida</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
